import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { useSessionTokenHash } from '@/hooks/useSessionTokenHash';
import { supabase } from '@/lib/supabase';
import type { SessionSettings } from '@/types/sessions';
import {
  applyIdleTimeoutOverride,
  readIdleTimeoutOverrideSeconds,
} from '@/lib/idleTimeoutOverride';
import { readAuthEpoch } from '@/lib/sessionExpiry';

interface UseSessionManagerOptions {
  onTimeout?: () => void;
  onWarning?: (remainingSeconds: number) => void;
  clinicId?: string | null;
  /** When false, skips staff session tracking (e.g. patient portal). */
  enabled?: boolean;
}

interface SessionManagerState {
  isActive: boolean;
  showWarning: boolean;
  remainingSeconds: number;
  settings: SessionSettings | null;
}

/**
 * Core session management hook that handles:
 * - Activity tracking via heartbeat
 * - Inactivity timeout detection
 * - Warning before timeout
 * - Automatic logout on timeout
 * - Session revocation checking
 */
export function useSessionManager(options: UseSessionManagerOptions = {}) {
  const enabled = options.enabled ?? true;
  const { session, signOut } = useAuth();
  const onTimeoutRef = useRef(options.onTimeout);
  const onWarningRef = useRef(options.onWarning);
  onTimeoutRef.current = options.onTimeout;
  onWarningRef.current = options.onWarning;

  const [state, setState] = useState<SessionManagerState>({
    isActive: true,
    showWarning: false,
    remainingSeconds: 0,
    settings: null,
  });

  const lastActivityRef = useRef<number>(Date.now());
  const hadSessionRef = useRef(false);
  const appliedAuthEpochRef = useRef<number | null>(null);
  const heartbeatIntervalRef = useRef<number | null>(null);
  const timeoutCheckIntervalRef = useRef<number | null>(null);
  const signingOutRef = useRef(false);
  const sessionTokenHash = useSessionTokenHash(session?.access_token);

  // Time spent signed out (the login form, the idle period that already fired)
  // must not count toward the next session. A newer sign-in epoch — including
  // one from another tab — restarts the clock too, so a stale timer cannot
  // sign the new session out immediately.
  const hasSession = Boolean(session);
  if (hasSession && !hadSessionRef.current) {
    lastActivityRef.current = Date.now();
    signingOutRef.current = false;
  }
  if (hasSession) {
    const authEpoch = readAuthEpoch();
    if (appliedAuthEpochRef.current !== null && authEpoch !== appliedAuthEpochRef.current) {
      lastActivityRef.current = Date.now();
      signingOutRef.current = false;
    }
    appliedAuthEpochRef.current = authEpoch;
    hadSessionRef.current = true;
  } else {
    hadSessionRef.current = false;
    signingOutRef.current = false;
  }

  // Load session settings
  useEffect(() => {
    if (!enabled || !session) return;

    const loadSettings = async () => {
      try {
        const { data, error } = await supabase.rpc('get_session_settings', {
          p_clinic_id: options.clinicId ?? undefined,
        });

        if (error) throw error;
        setState((prev) => ({
          ...prev,
          settings: applyIdleTimeoutOverride(data as SessionSettings),
        }));
      } catch (err) {
        console.error('Failed to load session settings:', err);
        // Use sensible defaults if settings can't be loaded
        setState((prev) => ({
          ...prev,
          settings: applyIdleTimeoutOverride({
            id: '',
            clinic_id: null,
            inactivity_timeout_minutes: 30,
            warning_before_timeout_minutes: 5,
            absolute_timeout_hours: 12,
            allow_trusted_devices: true,
            trusted_device_duration_days: 30,
            force_logout_on_ip_change: false,
            max_concurrent_sessions: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }),
        }));
      }
    };

    void loadSettings();
  }, [enabled, session, options.clinicId]);

  // Track user activity
  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setState((prev) => ({ ...prev, isActive: true, showWarning: false }));
  }, []);

  // Send heartbeat to server
  const sendHeartbeat = useCallback(async () => {
    if (!sessionTokenHash || signingOutRef.current) return;

    try {
      await supabase.rpc('update_session_activity', {
        p_session_token: sessionTokenHash,
      });
      // Do not reset lastActivityRef here — a successful heartbeat is not
      // user activity. Resetting it previously made inactivity timeout unreachable.
    } catch (err) {
      console.error('Failed to send heartbeat:', err);
    }
  }, [sessionTokenHash]);

  const beginForcedSignOut = useCallback(
    (reason: 'inactivity' | 'revoked') => {
      if (signingOutRef.current) return;
      signingOutRef.current = true;
      setState((prev) => ({ ...prev, isActive: false, showWarning: false }));
      void signOut({ reason }).catch((err) => {
        console.error('Forced sign-out failed:', err);
      });
    },
    [signOut],
  );

  // Check if session should timeout
  const checkTimeout = useCallback(() => {
    if (!state.settings || signingOutRef.current) return;

    const now = Date.now();
    const inactiveMs = now - lastActivityRef.current;
    const inactiveMinutes = inactiveMs / 1000 / 60;

    const timeoutMinutes = state.settings.inactivity_timeout_minutes;
    const warningMinutes = state.settings.warning_before_timeout_minutes;

    // Calculate remaining time
    const remainingMinutes = timeoutMinutes - inactiveMinutes;
    const remainingSeconds = Math.floor(remainingMinutes * 60);

    // If past timeout, logout
    if (remainingMinutes <= 0) {
      onTimeoutRef.current?.();
      beginForcedSignOut('inactivity');
      return;
    }

    // If within warning window, show warning
    if (remainingMinutes <= warningMinutes) {
      setState((prev) => ({
        ...prev,
        showWarning: true,
        remainingSeconds,
      }));
      onWarningRef.current?.(remainingSeconds);
    } else {
      setState((prev) => ({ ...prev, showWarning: false }));
    }
  }, [state.settings, beginForcedSignOut]);

  // Extend session (reset activity timer)
  const extendSession = useCallback(() => {
    recordActivity();
    void sendHeartbeat();
  }, [recordActivity, sendHeartbeat]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled || !session) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((event) => {
      window.addEventListener(event, recordActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, recordActivity);
      });
    };
  }, [enabled, session, recordActivity]);

  // Set up heartbeat interval (every 60 seconds)
  useEffect(() => {
    if (!enabled || !session || !sessionTokenHash) return;

    // Send initial heartbeat
    void sendHeartbeat();

    heartbeatIntervalRef.current = window.setInterval(sendHeartbeat, 60_000);

    return () => {
      if (heartbeatIntervalRef.current !== null) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [enabled, session, sessionTokenHash, sendHeartbeat]);

  // Set up timeout check interval (every 10 seconds, or 1s when testing a short override)
  useEffect(() => {
    if (!enabled || !session || !state.settings) return;

    const overrideSec = readIdleTimeoutOverrideSeconds();
    const intervalMs = overrideSec != null ? 1_000 : 10_000;

    checkTimeout();
    timeoutCheckIntervalRef.current = window.setInterval(checkTimeout, intervalMs);

    return () => {
      if (timeoutCheckIntervalRef.current !== null) {
        clearInterval(timeoutCheckIntervalRef.current);
      }
    };
  }, [enabled, session, state.settings, checkTimeout]);

  // Check for session revocation on mount and periodically
  useEffect(() => {
    if (!enabled || !session || !sessionTokenHash) return;

    const checkRevocation = async () => {
      if (signingOutRef.current) return;
      try {
        const { data, error } = await supabase
          .from('user_sessions')
          .select('is_revoked')
          .eq('session_token', sessionTokenHash)
          .maybeSingle();

        if (error) {
          // Session row missing or RLS after the JWT is gone — do not hang.
          return;
        }

        if (data?.is_revoked) {
          console.log('Session was revoked, logging out');
          beginForcedSignOut('revoked');
        }
      } catch (err) {
        console.error('Failed to check session revocation:', err);
      }
    };

    void checkRevocation();
    const interval = setInterval(checkRevocation, 30_000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [enabled, session, sessionTokenHash, beginForcedSignOut]);

  return {
    ...state,
    extendSession,
    recordActivity,
  };
}
